import {
  afterRenderEffect,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {NgClass} from '@angular/common';
import {CHIP_BASE} from '../ta-comment-text';
import {
  matchMentionPeople,
  mentionInitials,
  mentionQueryAt,
  MentionPerson,
  MentionRef,
  mentionRunToDelete,
  mentionsIn,
  parseCommentSegments,
  prunedAccepted,
  toDisplayText,
  toStoredText,
} from '../../../../util/ta-comment.util';

/**
 * How the box presents itself. 'default' is the composer's look - the comment glyph inside the
 * field. 'plain' drops it, for the places where the box is not the page's one obvious comment
 * field and the horizontal space is worth more than the affordance.
 */
export type TaCommentInputVariant = 'default' | 'plain';

/** How far the picker sits from the box it belongs to. */
const PICKER_GAP_PX = 4;

/** The picker's own max-h-56 plus its padding and heading, in numbers - used to decide which way to open. */
const PICKER_HEIGHT_PX = 288;

/**
 * The field's line height, in numbers - what `rows` is multiplied by to get the box's starting
 * height. Tighter than the 20px a comment is rendered at: the box holds two lines before it grows,
 * and at 20px those two lines make a noticeably taller box than the one-line field it replaces.
 */
const LINE_HEIGHT_PX = 18;

/** The box's own vertical padding and border, which its starting height has to clear as well. */
const BOX_CHROME_PX = 6;

/** The data attribute a chip is recognised by when the field's content is read back. */
const MENTION_ID_KEY = 'mentionId';

/** Elements a browser may wrap a new line in when one is made inside a contenteditable box. */
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI']);

/** What the field holds: its display text, and where the caret sits in it. */
interface FieldContent {
  text: string;
  caret: number;
}

/**
 * The comment box: one field, and the @mention picker that goes with it. Behind both the composer at
 * the top of the chain and the inline editor on a comment the user has opened, so a mention can be
 * added while writing and is still a mention after an edit.
 *
 * `value` is the *stored* text - mentions as `@[Name](id)` tokens, ready to post. What the user sees
 * is the display text, mentions flattened to `@Name` and drawn as the same cards a posted comment
 * shows, held on the field itself. The two are converted on every keystroke, so the host never sees
 * display text and never has to know the format; see ta-comment.util.ts.
 *
 * The field is a contenteditable box rather than a textarea, because only an element tree can carry
 * those cards. Each is a `contenteditable="false"` span, which is what makes the browser treat a
 * mention as one object: an arrow key steps over the whole card and a selection takes all of it or
 * none. Deleting it is handled here rather than left to the browser, which varies on whether one
 * keystroke removes such a span or only selects it first.
 *
 * Two looks, per `variant`: the composer's, with the comment glyph in the field, and 'plain'
 * without it. Placeholder text, starting height and a disabled field are set per host either way.
 *
 * Only a mention this box knows about becomes a token - one picked from the list, or one the
 * comment already carried when it opened. `accepted` is that set: typing `@Ron Bean` out by
 * hand, or editing over a mention that was there, leaves plain text.
 */
@Component({
  selector: 'ta-comment-input',
  imports: [NgClass],
  templateUrl: './ta-comment-input.html',
  host: {
    class: 'block w-full min-w-0',
    // The picker is placed in viewport coordinates off a box that moves when the window does, and
    // it is short-lived enough that re-measuring it is not worth a scroll listener.
    '(window:resize)': 'closePicker()',
  },
})
export class TaCommentInputComponent {
  /** The comment as it will be stored - mentions as tokens. Two-way, so the host owns the draft. */
  value = model<string>('');
  /** Who can be mentioned. The picker's list - a mention already in the text carries its own name. */
  people = input<readonly MentionPerson[]>([]);
  /**
   * Whether that list is still on its way. Hosts fetch it when the box is focused, so an @ typed
   * straight away can beat it here; while this is set the picker opens on a loading row rather than
   * staying shut as it would for a list that is genuinely empty.
   */
  peopleLoading = input<boolean>(false);
  /** The box's own prompt text. Empty leaves the field bare. */
  placeholder = input<string>('');
  /** Makes the field read-only-and-dead: no typing, no picker, no submit. */
  disabled = input<boolean>(false);
  /**
   * How many lines the box stands at before it grows. Two by default: a comment with a mention or
   * two in it runs to a second line constantly, and a box that jumps taller on the wrap of every
   * other sentence is the thing that reads as unstable. Past this the field grows a line at a time,
   * up to its own max-height.
   */
  rows = input<number>(2);
  variant = input<TaCommentInputVariant>('default');
  testId = input<string>('ta-comment-input');

  protected showIcon = computed(() => this.variant() === 'default');
  protected fillClass = computed(() => this.disabled()
    ? 'bg-[var(--p-form-field-disabled-background)]'
    : 'bg-white');
  /**
   * The box's border. Teal on the editor's variant, and teal through hover and focus alike: there
   * it marks the comment as the one being edited rather than reacting to the pointer. The composer's
   * follows the field instead - grey, darker under the pointer, primary while it holds the caret -
   * and only while it is live, so a dead box does not light up when the pointer crosses it.
   */
  protected borderClass = computed(() => {
    if (this.variant() === 'plain') {
      return 'border-teal-600';
    }
    return this.disabled()
      ? 'border-surface-300'
      : 'border-surface-300 hover:border-surface-400 focus-within:border-primary-500';
  });
  /** The box's height at rest - the rows it holds, plus its own padding and border. */
  protected minHeightPx = computed(() => this.rows() * LINE_HEIGHT_PX + BOX_CHROME_PX);

  /** Enter on a closed picker. The host decides what sending means. */
  submitted = output<void>();
  /** Mirrors the field's focus, which the composer's send button reads. */
  focusChange = output<boolean>();

  private box = viewChild<ElementRef<HTMLElement>>('box');
  private field = viewChild<ElementRef<HTMLElement>>('field');

  /**
   * What should be in the field: the comment with its mentions flattened to `@Name`. The field is
   * rebuilt from this only when the two differ, rather than bound to it - rebuilding on every
   * keystroke would throw away the caret, the browser's undo history and any composition in flight.
   * Also what the placeholder is shown or hidden by, a contenteditable box having none of its own.
   */
  protected displayText = signal<string>('');

  protected pickerOpen = signal<boolean>(false);
  protected suggestions = signal<MentionPerson[]>([]);
  protected activeIndex = signal<number>(0);
  /** Where the picker sits. One of top / bottom is set and the other left null, so it can flip up. */
  protected pickerTop = signal<number | null>(null);
  protected pickerBottom = signal<number | null>(null);
  protected pickerLeft = signal<number>(0);
  protected pickerWidth = signal<number>(0);

  /**
   * The mentions in play, and so the only `@Name` runs that become tokens - and the only ones drawn
   * as cards: the ones picked from the list, plus the ones the comment already carried when the
   * editor opened on it.
   */
  private accepted: MentionRef[] = [];
  /** The mention run under the caret that a pick replaces. */
  private activeQuery?: {query: string; start: number};
  /** The last text emitted upwards, so the host echoing it back is not read as an external change. */
  private lastEmitted = '';

  constructor() {
    // Only an incoming value the host set itself resets the field - the editor opening on a
    // comment, or the composer clearing after a post. Nothing else is read here, so a people list
    // landing mid-sentence cannot wipe what is being typed.
    effect(() => {
      const incoming = this.value();
      if (incoming === this.lastEmitted) {
        return;
      }
      this.resetFrom(incoming);
    });

    // The list can land while the picker is already open on its loading row, so it is re-matched
    // against the field as it stands rather than waiting for another keystroke. Only the picker is
    // touched here - the field itself is left exactly as the user has it.
    effect(() => {
      this.people();
      this.peopleLoading();
      untracked(() => {
        const field = this.field()?.nativeElement;
        if (!field || !this.pickerOpen()) {
          return;
        }
        this.refreshPicker(readField(field));
      });
    });

    // After render, because the field may not exist yet when a host seeds a draft into `value` -
    // the chain panel builds its composer only as it opens.
    afterRenderEffect(() => this.syncField());
  }

  /** Puts the cursor in the field, at the end of whatever is already there. */
  focus(): void {
    const field = this.field()?.nativeElement;
    if (!field) {
      return;
    }
    field.focus();
    placeCaret(field, this.displayText().length);
  }

  protected onInput(): void {
    this.syncFromField();
  }

  /**
   * Rich text pasted into a contenteditable box arrives as markup; only its words are wanted.
   * insertText rather than writing the DOM directly because it is the one insertion the browser's
   * own undo stack still knows about.
   */
  protected onPaste(event: ClipboardEvent): void {
    const field = this.field()?.nativeElement;
    const text = event.clipboardData?.getData('text/plain');
    if (!field || text === undefined) {
      return;
    }
    event.preventDefault();
    if (!document.execCommand('insertText', false, text)) {
      insertTextAtCaret(field, text);
      this.syncFromField();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    // Mid-composition every key belongs to the IME - Enter and the arrows are it choosing a
    // candidate, not the user sending a comment or moving through the picker.
    if (event.isComposing) {
      return;
    }

    if (!this.pickerOpen()) {
      // Shift+Enter is the only way to a newline, so Enter can stay "send" the way it always has.
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.submitted.emit();
      } else if (event.key === 'Backspace') {
        this.deleteMention(event, 'backward');
      } else if (event.key === 'Delete') {
        this.deleteMention(event, 'forward');
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
      case 'Tab': {
        const person = this.suggestions()[this.activeIndex()];
        if (person) {
          event.preventDefault();
          this.accept(person);
          break;
        }
        // Nothing to accept: the picker is only up on its loading row. The key keeps the meaning it
        // has everywhere else rather than being swallowed for the length of the fetch.
        this.closePicker();
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          this.submitted.emit();
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        // Stopped here or it reaches the document listener that collapses the whole chain panel.
        event.stopPropagation();
        this.closePicker();
        break;
    }
  }

  protected onBlur(): void {
    this.focusChange.emit(false);
    this.closePicker();
  }

  /** Swaps the half-typed `@query` under the caret for the person's name and records the mention. */
  protected accept(person: MentionPerson): void {
    const field = this.field()?.nativeElement;
    const query = this.activeQuery;
    if (!field || !query) {
      return;
    }

    const display = this.displayText();
    const before = display.slice(0, query.start);
    const after = display.slice(query.start + 1 + query.query.length);
    // Trailing space so the next word is not read as part of the name still being typed, and so
    // there is somewhere to put the caret on the far side of the card.
    const inserted = `@${person.name} `;

    // Matched on the name as well as the id: `accepted` can already hold this person under the name
    // an older mention of them was made under, and it is the name that `@Ron Bean ` below has
    // to be matched by later. Skipping on the id alone would insert a run nothing can claim.
    if (!this.accepted.some(existing => existing.id === person.id && existing.name === person.name)) {
      this.accepted = [...this.accepted, {id: person.id, name: person.name}];
    }

    this.closePicker();
    field.focus();
    this.setField(field, before + inserted + after, before.length + inserted.length);
  }

  /**
   * A delete keystroke against a mention removes the whole of it rather than a character, so the
   * mention behaves as the one object it reads as. A non-empty selection is left alone: that
   * deletes exactly what is selected, mentions or not.
   *
   * Two shapes to remove. A mention the box knows about is a card, and the card is taken out whole -
   * browsers disagree on whether one keystroke removes a non-editable span or only selects it, so
   * this is not left to them. A mention still sitting in the text as `@Name` - typed out by hand
   * under a name already accepted, so still a token once stored - is removed by its run instead.
   */
  private deleteMention(event: KeyboardEvent, direction: 'backward' | 'forward'): void {
    const field = this.field()?.nativeElement;
    const selection = window.getSelection();
    if (!field || !selection || !selection.isCollapsed) {
      return;
    }

    const chip = chipBesideCaret(field, selection, direction);
    if (chip?.parentNode) {
      event.preventDefault();
      const parent = chip.parentNode;
      const index = [...parent.childNodes].indexOf(chip);
      chip.remove();
      collapseSelectionTo(selection, parent, index);
      this.syncFromField();
      return;
    }

    const display = this.displayText();
    const run = mentionRunToDelete(display, readField(field).caret, direction, this.accepted);
    if (!run) {
      return;
    }
    event.preventDefault();
    const next = display.slice(0, run.start) + display.slice(run.end);
    this.setField(field, next, run.start);
    this.refreshPicker({text: next, caret: run.start});
  }

  protected closePicker(): void {
    this.pickerOpen.set(false);
    this.suggestions.set([]);
    this.activeQuery = undefined;
  }

  /** Opens, moves or closes the picker for whatever is under the caret now. */
  private refreshPicker(content: FieldContent): void {
    const query = mentionQueryAt(content.text, content.caret);
    if (!query) {
      this.closePicker();
      return;
    }

    // No candidates closes it, which is what stops a query with spaces in it running on into prose -
    // unless the list is still coming, where the picker stays up on its loading row instead of
    // flicking shut a moment before the people land.
    const matches = matchMentionPeople(this.people(), query.query);
    if (matches.length === 0 && !this.peopleLoading()) {
      this.closePicker();
      return;
    }

    this.activeQuery = query;
    this.suggestions.set(matches);
    this.activeIndex.set(0);
    this.measurePicker();
    this.pickerOpen.set(true);
  }

  /** Wraps, so holding ArrowDown cycles rather than sticking at the bottom. */
  private moveActive(step: number): void {
    const count = this.suggestions().length;
    if (count === 0) {
      return;
    }
    this.activeIndex.update(index => (index + step + count) % count);
  }

  /** Under the box, or above it when there is not room below - the editor can sit low on the page. */
  private measurePicker(): void {
    const box = this.box()?.nativeElement;
    if (!box) {
      return;
    }
    const rect = box.getBoundingClientRect();
    this.pickerLeft.set(rect.left);
    this.pickerWidth.set(rect.width);

    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < PICKER_HEIGHT_PX && rect.top > spaceBelow) {
      this.pickerTop.set(null);
      this.pickerBottom.set(window.innerHeight - rect.top + PICKER_GAP_PX);
    } else {
      this.pickerTop.set(rect.bottom + PICKER_GAP_PX);
      this.pickerBottom.set(null);
    }
  }

  /**
   * Loads stored text into the field and re-seeds the mentions it already carries as accepted.
   * Both come out of the tokens themselves, so an editor opened before the picker's list has landed
   * still draws its mentions as cards, and still deletes them in one keystroke.
   */
  private resetFrom(stored: string): void {
    this.lastEmitted = stored;
    this.accepted = mentionsIn(stored);
    this.displayText.set(toDisplayText(stored));
    this.closePicker();
  }

  /**
   * Display text back out as stored text. Accepted mentions the user has since edited away are
   * dropped first, so their `@Name` cannot be re-tokenised if it is typed again by hand.
   */
  private emit(display: string): void {
    this.accepted = prunedAccepted(display, this.accepted);
    const stored = toStoredText(display, this.accepted);
    this.lastEmitted = stored;
    this.value.set(stored);
  }

  /** Takes what the user has just done to the field as the new truth, picker and all. */
  private syncFromField(): void {
    const field = this.field()?.nativeElement;
    if (!field) {
      return;
    }
    const content = readField(field);
    this.displayText.set(content.text);
    this.emit(content.text);
    this.refreshPicker(content);
  }

  /**
   * Rebuilds the field from display text, keeps the host's draft in step with it, and places the
   * caret. Everything that edits the text for the user rather than through a keystroke goes through
   * here; a rebuild is what turns a newly accepted mention into a card.
   */
  private setField(field: HTMLElement, display: string, caret: number): void {
    this.displayText.set(display);
    // Before the rebuild: this is what prunes `accepted`, and `accepted` is what decides which runs
    // are drawn as cards.
    this.emit(display);
    renderField(field, display, this.accepted);
    placeCaret(field, caret);
  }

  /**
   * Puts `displayText` into the field if it is not already there. The guard is what keeps typing
   * and picking a mention from resetting the caret: both leave the field holding that text already,
   * so this finds nothing to do, and only a value the host set from outside gets this far.
   */
  private syncField(): void {
    const field = this.field()?.nativeElement;
    if (!field) {
      return;
    }
    const display = this.displayText();
    if (readField(field).text === display) {
      return;
    }
    renderField(field, display, this.accepted);
    // Only if the field already holds the caret - moving the selection into it otherwise would take
    // focus off whatever has it.
    if (document.activeElement === field) {
      placeCaret(field, display.length);
    }
  }

  protected readonly mentionInitials = mentionInitials;
}

/** One mention, as the card the field carries it as. */
function mentionChip(userId: string, name: string): HTMLElement {
  const chip = document.createElement('span');
  // The whole point: the browser gives a non-editable span no caret positions of its own, so an
  // arrow key steps over it and a selection takes all of it or none.
  chip.contentEditable = 'false';
  chip.dataset[MENTION_ID_KEY] = userId;
  //See ta-comment-text.ts mentionClass for other formatting of chips
  chip.className = `${CHIP_BASE} bg-primary-100 text-primary-800`;
  chip.textContent = `@${name}`;
  return chip;
}

/**
 * Draws display text into the field: a card per accepted mention, a text node for the prose between.
 * Routed through the stored form so that what the field shows and what will be posted are decided
 * by one piece of logic rather than two.
 *
 * Newlines stay as newlines in the text nodes - the field is whitespace-pre-wrap, as the rendered
 * comment is - so an offset into the display text is an offset into these nodes.
 */
function renderField(root: HTMLElement, display: string, accepted: readonly MentionRef[]): void {
  const nodes = parseCommentSegments(toStoredText(display, accepted)).map(segment =>
    segment.kind === 'mention'
      ? mentionChip(segment.userId, segment.name)
      : document.createTextNode(segment.text));
  root.replaceChildren(...nodes);
}

/**
 * The field's content as display text, with the caret's position in it.
 *
 * The DOM is read rather than tracked because between rebuilds the browser owns it: typing, native
 * deletes and undo all edit it directly. Anything it leaves behind is folded back to text here - a
 * card reads as its own `@Name`, a `<br>` or a wrapping block as a newline, and the non-breaking
 * spaces some browsers use to hold a run of spaces open as the spaces they stand for.
 */
function readField(root: HTMLElement): FieldContent {
  const selection = window.getSelection();
  const focus = selection?.focusNode && root.contains(selection.focusNode)
    ? {node: selection.focusNode, offset: selection.focusOffset}
    : undefined;

  let text = '';
  let caret = -1;

  const visit = (node: Node): void => {
    const children = [...node.childNodes];
    children.forEach((child, index) => {
      if (focus && focus.node === node && focus.offset === index) {
        caret = text.length;
      }

      if (child.nodeType === Node.TEXT_NODE) {
        const value = child.nodeValue ?? '';
        if (focus && focus.node === child) {
          caret = text.length + Math.min(focus.offset, value.length);
        }
        text += value;
        return;
      }
      if (!(child instanceof HTMLElement)) {
        return;
      }
      if (child.dataset[MENTION_ID_KEY]) {
        // A caret inside a card belongs at its edge - the card has no inside as far as the text goes.
        if (focus && (focus.node === child || child.contains(focus.node))) {
          caret = text.length;
        }
        text += child.textContent ?? '';
        return;
      }
      if (child.tagName === 'BR') {
        // Except the one a browser parks at the very end to keep an empty last line visible, which
        // is not a newline anybody typed.
        if (child !== root.lastChild) {
          text += '\n';
        }
        return;
      }
      if (BLOCK_TAGS.has(child.tagName) && text && !text.endsWith('\n')) {
        text += '\n';
      }
      visit(child);
    });
    if (focus && focus.node === node && focus.offset === children.length) {
      caret = text.length;
    }
  };
  visit(root);

  // Length-preserving, so the caret found above still points where it did.
  const display = text.replace(/\u00a0/g, ' ');
  return {text: display, caret: caret < 0 ? display.length : caret};
}

/** Puts the caret at an offset into the field's display text. */
function placeCaret(root: HTMLElement, offset: number): void {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  let remaining = offset;
  const children = [...root.childNodes];
  for (const [index, node] of children.entries()) {
    const length = (node.textContent ?? '').length;
    if (remaining <= length) {
      if (node.nodeType === Node.TEXT_NODE) {
        collapseSelectionTo(selection, node, remaining);
      } else {
        // A card holds no caret, so an offset inside one resolves to the side it is nearest.
        collapseSelectionTo(selection, root, remaining === 0 ? index : index + 1);
      }
      return;
    }
    remaining -= length;
  }
  collapseSelectionTo(selection, root, children.length);
}

/** Collapses the selection to one point, which is all this field ever sets it to. */
function collapseSelectionTo(selection: Selection, node: Node, offset: number): void {
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * The mention card a delete keystroke is up against, if any. Only a caret hard against the card
 * counts: anywhere else in a text node there is a character to delete first.
 */
function chipBesideCaret(
  root: HTMLElement,
  selection: Selection,
  direction: 'backward' | 'forward',
): HTMLElement | undefined {
  const {focusNode, focusOffset} = selection;
  if (!focusNode || !root.contains(focusNode)) {
    return undefined;
  }

  let node: Node | null;
  if (focusNode.nodeType === Node.TEXT_NODE) {
    const length = (focusNode.nodeValue ?? '').length;
    if (direction === 'backward' ? focusOffset > 0 : focusOffset < length) {
      return undefined;
    }
    node = direction === 'backward' ? focusNode.previousSibling : focusNode.nextSibling;
  } else {
    const index = direction === 'backward' ? focusOffset - 1 : focusOffset;
    node = focusNode.childNodes[index] ?? null;
  }

  // Empty text nodes an edit has left behind sit between the caret and the card without being
  // anything the keystroke should take instead.
  while (node && node.nodeType === Node.TEXT_NODE && (node.nodeValue ?? '') === '') {
    node = direction === 'backward' ? node.previousSibling : node.nextSibling;
  }
  return node instanceof HTMLElement && node.dataset[MENTION_ID_KEY] ? node : undefined;
}

/** Types text in at the caret. Only reached where the browser will not do it itself. */
function insertTextAtCaret(root: HTMLElement, text: string): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }
  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) {
    return;
  }
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  collapseSelectionTo(selection, node, text.length);
}
